import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { installClientErrorCapture } from "@/lib/clientErrorCapture";
import App from "./App.tsx";
import { isFullwidthPath } from "@/lib/fullwidth-routes";
import {
  isPreviewHost,
  renderBootFallback,
  installChunkLoadRecovery,
  checkDeploymentAndRecover,
} from "@/lib/startupRecovery";
import "./index.css";

// Bumped on each release-critical change so the published bundle hash changes
// and stale CDN copies don't trap users on a white screen.
const __BUILD_ID = "boot-recovery-2026-04-25";
try { console.debug("[boot] build", __BUILD_ID); } catch { /* ignore */ }

// Install global error listeners ASAP so uncaught errors before React mounts are still captured
try { installClientErrorCapture(); } catch { /* ignore */ }
// Catch chunk-load failures (stale deploy, flaky network) and self-heal once.
try { installChunkLoadRecovery(); } catch { /* ignore */ }

// Apply fullwidth class synchronously before React renders so direct URL
// loads (e.g. /auth in a new tab) don't briefly inherit the phone-frame layout.
if (typeof window !== "undefined") {
  const rootEl = document.getElementById("root");
  if (rootEl && isFullwidthPath(window.location.pathname)) {
    rootEl.classList.add("fullwidth");
  }
}

const clearPreviewCaches = async () => {
  if (typeof window === "undefined") return;
  // Only run on actual preview hosts — never on the published *.lovable.app
  // domain or custom domains. This previously wiped caches on production too.
  if (!isPreviewHost(window.location.hostname)) return;

  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }

    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    }

    const reloadKey = "preview-cache-reset-v1";
    if (sessionStorage.getItem(reloadKey) !== "done") {
      sessionStorage.setItem(reloadKey, "done");
      window.location.reload();
    }
  } catch (error) {
    console.warn("[preview-cache-reset] Failed to clear preview caches", error);
  }
};

const mount = () => {
  const container = document.getElementById("root");
  if (!container || (container as any).__reactRoot) return;

  try {
    const root = createRoot(container);
    (container as any).__reactRoot = root;
    root.render(
      <HelmetProvider>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </HelmetProvider>
    );
    try { (window as any).__appMounted = true; } catch { /* ignore */ }
  } catch (err) {
    try { console.error("[bootstrap] Failed to mount React app", err); } catch { /* ignore */ }
    try {
      renderBootFallback(
        container,
        "The app couldn't start. Try reloading, or reset local data if the problem keeps happening."
      );
    } catch { /* ignore */ }
  }
};

// Production deployment-mismatch guard: runs BEFORE React mounts so a
// returning user on stale HTML/chunks gets reset+reloaded instead of
// rendering a white screen and waiting for the watchdog.
clearPreviewCaches()
  .catch(() => { /* ignore */ })
  .then(async () => {
    try {
      const recovered = await checkDeploymentAndRecover();
      // If recovery triggered a reload, don't bother mounting — the page
      // is about to navigate away.
      if (recovered) return;
    } catch { /* ignore */ }
    mount();
  });
